import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import * as cssTable from '@delmonicos/shared-design-system-react/src/components/table/table.module.scss';
import { GatewayChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-point.model';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';

import { ChargePointDeleteController } from '../charge-point-delete/charge-point-delete.controller';
import { ChargePointContextProvider } from '../charge-point.context';

export interface ChargePointListActionsCellProps
  extends CellContext<GatewayChargePointModel, unknown> {}

export const ChargePointListActionsCell = (
  props: ChargePointListActionsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canDelete =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.CHARGE_POINTS,
      action: ActionsByResource.CHARGE_POINTS.DELETE,
    });

  const canRead =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.CHARGE_POINTS,
      action: ActionsByResource.CHARGE_POINTS.READ,
    });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <ChargePointContextProvider data={row.original}>
      <Stack direction="row" justifyContent="flex-end">
        {canRead && (
          <ButtonIcon
            icon="eye"
            color={ButtonColor.Ghost}
            onClick={() =>
              navigate(
                generatePath(APP_PATH.CHARGE_POINT.DETAILS, {
                  id: row.original.id,
                }),
              )
            }
            className={cssTable.displayOnHover}
          />
        )}
        {canDelete && (
          <>
            <ButtonIcon
              icon="trash-01"
              color={ButtonColor.Ghost}
              onClick={() => setActionDeleteIsDialogOpen(true)}
              className={cssTable.displayOnHover}
            />

            <Dialog
              onClose={() => setActionDeleteIsDialogOpen(true)}
              open={!!actionDeleteIsDialogOpen}>
              <Dialog.Header
                title={t('$.charge-point-delete.title')}
                icon="map-01"
              />
              <Dialog.Content>
                <ChargePointDeleteController
                  onCancel={() => setActionDeleteIsDialogOpen(false)}
                  onSuccess={() => setActionDeleteIsDialogOpen(false)}
                />
              </Dialog.Content>
            </Dialog>
          </>
        )}
      </Stack>
    </ChargePointContextProvider>
  );
};
